import React, { useEffect, useMemo, useRef, useState } from "react";
import globalAxios from "../../globalAxios.js";
import { SERVER_URL } from "../../config.js";

import SessionView from "../session/SessionView.tsx";
import EventView from "../event/EventView.tsx";
import PatientView from "../patient/PatientView.tsx";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import listPlugin from "@fullcalendar/list";
import interactionPlugin from "@fullcalendar/interaction";

import { startOfWeek, isSameWeek } from "date-fns";

import { useAppSelector, useAppDispatch } from "../../store/hooks.ts";
import {
	setCalendarLens,
	setCalendarView,
	setCalendarDate,
	deleteEventsByMonth,
	lensOptions,
	setSelectedEntityType,
	setWeekCycleLabel,
	setCreateDate,
} from "../../store/slices/calendarSlice.ts";

import { setSelectedSession } from "../../store/slices/sessionSlice.ts";

import {
	contentToDisplay,
	formatEvents,
	setEventColor,
} from "./helper/eventRendering.tsx";

import { expandRegularSessions } from "./helper/regularSessionExpansion.ts";
import { selectRegularSessions } from "../../store/slices/regularSessionsSlice.ts";

import {
	Box,
	Button,
	ButtonGroup,
	CircularProgress,
	IconButton,
	Menu,
	MenuItem,
	Select,
	Typography,
	Popover,
} from "@mui/material";

import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { enGB } from "date-fns/locale";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

import "./Calendar.css";

import { setAnchorDate as setCalendarSliceAnchorDate } from "../../store/slices/calendarSlice.ts";
import { updateCalendarAnchorDate } from "../../store/slices/userSlice.ts";
import PersonalCreateView from "../personal/PersonalView.tsx";
import {
	selectPersonals,
	setSelectedPersonal,
} from "../../store/slices/personalsSlice.ts";
import PotentialRegularSessionDialog from "../../components/potentialRegularSessionModal/PotentialRegularSessionModal.tsx";
import { applySurgeonBillingRulesToSessions } from "./helper/surgeonBillingRules.ts";

/* ----------------- Local helpers ----------------- */

// Monday normaliser (local)
const startOfMonday = (d: Date | string) => {
	const date = new Date(d);
	const day = date.getDay(); // 0=Sun, 1=Mon, ...
	const diff = day === 0 ? -6 : 1 - day;
	date.setDate(date.getDate() + diff);
	date.setHours(0, 0, 0, 0);
	return date;
};

// Local midnight
const toLocalMidnight = (x: Date | string) => {
	const d = new Date(x);
	return new Date(d.getFullYear(), d.getMonth(), d.getDate());
};

const toISODate = (d: Date) => {
	const y = d.getFullYear();
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${y}-${m}-${day}`;
};

// Parse "YYYY-MM-DD" as local
const parseISODateLocal = (iso: string) => {
	const [y, m, d] = iso.split("-").map(Number);
	const dt = new Date(y, (m ?? 1) - 1, d ?? 1);
	dt.setHours(0, 0, 0, 0);
	return dt;
};

// ISO week helpers
const weeksInISOYear = (isoYear: number) => {
	const d = new Date(Date.UTC(isoYear, 11, 28));
	d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
	const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
	return Math.round(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
};

const isoWeekInfo = (dateIn: Date) => {
	const d = new Date(
		Date.UTC(dateIn.getFullYear(), dateIn.getMonth(), dateIn.getDate()),
	);
	d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
	const isoYear = d.getUTCFullYear();
	const yearStart = new Date(Date.UTC(isoYear, 0, 1));
	const weekRaw = Math.ceil(
		((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7,
	);

	const totalWeeks = weeksInISOYear(isoYear);
	const overlap = weekRaw === 53 && totalWeeks === 52;
	const week = overlap ? 1 : weekRaw;

	return { week, isoYear, overlap };
};

// DST-safe day maths (UTC midnights)
const utcMidnight = (d: Date) =>
	new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));

const calendarDayDiff = (a: Date, b: Date) =>
	Math.round(
		(utcMidnight(b).getTime() - utcMidnight(a).getTime()) / 86400000,
	);

// New: addDays & addMonths (DST-safe)
const addDaysSafe = (d: Date, days: number) => {
	const u = utcMidnight(d);
	const res = new Date(u.getTime() + days * 86400000);
	return new Date(res.getUTCFullYear(), res.getUTCMonth(), res.getUTCDate());
};

const addMonthsSafe = (d: Date, months: number) => {
	// Move to first of month to avoid month-end rollover weirdness (e.g., 31st)
	const base = new Date(d.getFullYear(), d.getMonth(), 1);
	const m = base.getMonth() + months;
	const target = new Date(base.getFullYear(), m, 1);
	return target;
};

// Align a 4-week block from anchor + focus (both Mondays)
const snapBlockStart = (anchorMonday: Date, focusMonday: Date) => {
	const base = startOfMonday(anchorMonday);
	const target = startOfMonday(focusMonday);
	const deltaDays = calendarDayDiff(base, target);
	const k = Math.floor(deltaDays / 28);
	const snapped = new Date(base);
	snapped.setDate(snapped.getDate() + k * 28);
	snapped.setHours(0, 0, 0, 0);
	console.debug("[Snap]", { base, target, deltaDays, k, snapped });
	return snapped;
};

// Letter from (anchor Monday → cell Monday)
const letterForMondayFromAnchor = (cellMonday: Date, anchorMonday: Date) => {
	const days = calendarDayDiff(anchorMonday, cellMonday);
	const weeks = Math.floor(days / 7);
	const idx = ((weeks % 4) + 4) % 4; // safe modulo for negatives
	return (["A", "B", "C", "D"] as const)[idx];
};

// In handledateset for handling regular session rendering.
const sameDay = (a: Date, b: Date) =>
	a.getFullYear() === b.getFullYear() &&
	a.getMonth() === b.getMonth() &&
	a.getDate() === b.getDate();

const addMonthsSafeForRecurrence = (d: Date, months: number) => {
	const next = new Date(d);
	next.setMonth(next.getMonth() + months);
	next.setHours(0, 0, 0, 0);
	return next;
};

const addYearsSafeForRecurrence = (d: Date, years: number) => {
	const next = new Date(d);
	next.setFullYear(next.getFullYear() + years);
	next.setHours(0, 0, 0, 0);
	return next;
};

const getNextPersonalOccurrenceDate = (
	date: Date,
	rule: string | null | undefined,
) => {
	switch (rule) {
		case "FREQ=DAILY":
			return addDaysSafe(date, 1);

		case "FREQ=WEEKLY":
			return addDaysSafe(date, 7);

		case "FREQ=WEEKLY;INTERVAL=2":
			return addDaysSafe(date, 14);

		case "FREQ=MONTHLY":
			return addMonthsSafeForRecurrence(date, 1);

		case "FREQ=YEARLY":
			return addYearsSafeForRecurrence(date, 1);

		default:
			return null;
	}
};

const getTimeFromEvent = (event: any, preferredField: "start" | "end") => {
	const explicit =
		preferredField === "start"
			? event?.startingTime || event?.start_time
			: event?.endingTime || event?.end_time;

	if (explicit) return explicit;

	const dateTime = preferredField === "start" ? event?.start : event?.end;
	if (typeof dateTime === "string" && dateTime.includes("T")) {
		return dateTime.slice(11, 16);
	}

	return null;
};

const getMonthBlockPlacement = (
	startTime?: string | null,
	endTime?: string | null,
) => {
	if (!startTime) return { slot: 0, span: 1 };

	const toMinutes = (time: string) => {
		const [h, m] = time.split(":").map(Number);
		return h * 60 + m;
	};

	const start = toMinutes(startTime);
	const end = endTime ? toMinutes(endTime) : start;

	const amStart = 8 * 60;
	const pmStart = 12 * 60 + 30;
	const twStart = 18 * 60;
	const twEnd = 22 * 60 + 30;

	const slot = start < pmStart ? 0 : start < twStart ? 1 : 2;

	const overlaps = [
		end > amStart && start < pmStart,
		end > pmStart && start < twStart,
		end > twStart && start < twEnd,
	];

	const span = Math.max(1, overlaps.filter(Boolean).length);

	return { slot, span };
};

const getMonthEventText = (event: any) => {
	const startTime = getTimeFromEvent(event, "start");

	if (event?.isPersonalEvent) {
		return [startTime, event.title].filter(Boolean).join(" - ");
	}

	const surgeonAcronym =
		event?.surgeon?.acronym ||
		[event?.surgeon?.first_name?.[0], event?.surgeon?.last_name?.[0]]
			.filter(Boolean)
			.join("");

	const abbreviateName = (name?: string | null) => {
		if (!name) return "";

		return name
			.split(/\s+/)
			.filter(Boolean)
			.map((word) => word[0]?.toUpperCase())
			.join("");
	};

	const hospitalAcronym =
		event?.hospital?.acronym || abbreviateName(event?.hospital?.name);

	return [startTime, surgeonAcronym, hospitalAcronym]
		.filter(Boolean)
		.join(" - ");
};

const Calendar = () => {
	const calendarRef = useRef<FullCalendar | null>(null);
	const lastGotoRef = useRef<number | null>(null);

	const weekCycleLabel = useAppSelector((s) => s.calendar.weekCycleLabel);

	const selectedEntityType = useAppSelector(
		(s) => s.calendar.selectedEntityType,
	);
	const selectedSession = useAppSelector((s) => s.session.selectedSession);
	const selectedEvent = useAppSelector((s) => s.event.selectedEvent);
	const selectedPatient = useAppSelector((s) => s.patient?.selectedPatient);

	const events = useAppSelector((s) => s.calendar.sessions);
	const regularSessions = useAppSelector(selectRegularSessions);
	const personals = useAppSelector(selectPersonals);

	const currentLens = useAppSelector((s) => s.calendar.lens);
	const currentView = useAppSelector((s) => s.calendar.view);
	const currentDate = useAppSelector((s) => s.calendar.currentDate);
	const isAdmin = useAppSelector((s) => s.user.admin);
	const user = useAppSelector((s) => s.user);

	const dispatch = useAppDispatch();

	const [schedules, setSchedules] = useState<any[]>([]);
	const [deletingEvents, setDeletingEvents] = useState(false);

	// Tooltip local state.
	const [helpAnchorEl, setHelpAnchorEl] = useState<HTMLElement | null>(null);
	const helpOpen = Boolean(helpAnchorEl);
	const helpId = helpOpen ? "anchor-help-popover" : undefined;

	// Stable initial anchor from settings
	const initialAnchor = (() => {
		let settings = user?.settings;
		if (typeof settings === "string") {
			try {
				settings = JSON.parse(settings);
			} catch {}
		}
		const iso: string | undefined = settings?.calendar?.anchorDate;
		const monday = iso
			? startOfMonday(parseISODateLocal(iso))
			: startOfMonday(new Date());
		console.debug("[Anchor:init]", { iso, monday });
		return monday;
	})();

	const [anchorDate, setAnchorDate] = useState<Date>(initialAnchor);
	const [savingAnchor, setSavingAnchor] = useState(false);

	// Keep refs for decorators to always see latest values
	const anchorRef = useRef(anchorDate);
	useEffect(() => {
		anchorRef.current = anchorDate;
	}, [anchorDate]);

	// Used for determining regular sessions to render.
	const [visibleRange, setVisibleRange] = useState({
		start: initialAnchor,
		end: addDaysSafe(initialAnchor, 28),
	});

	// Week Letter Label for Week and Day view
	const weekLetterFromDate = (d: Date, anchor: Date) => {
		const cellMon = startOfMonday(d);
		const anchorMon = startOfMonday(anchor);
		const days = calendarDayDiff(anchorMon, cellMon);
		const weeks = Math.floor(days / 7);
		const idx = ((weeks % 4) + 4) % 4;
		return (["A", "B", "C", "D"] as const)[idx];
	};

	/* ---------- Focus Monday & 4-week block ---------- */
	const focusMonday = useMemo(
		() =>
			startOfMonday(
				currentDate
					? toLocalMidnight(currentDate)
					: toLocalMidnight(new Date()),
			),
		[currentDate],
	);

	const blockStart = useMemo(
		() => snapBlockStart(anchorDate, focusMonday),
		[anchorDate, focusMonday],
	);

	const blockStartRef = useRef(blockStart);
	useEffect(() => {
		blockStartRef.current = blockStart;
	}, [blockStart]);

	/* ----------------- Schedules ----------------- */
	const extractItemNumbersFromRedux = (evts: any[]) => {
		const itemNumbers: string[] = [];
		evts.forEach((event) => {
			event.events.forEach((subEvent: any) => {
				try {
					if (
						!subEvent.actual_items ||
						subEvent.actual_items === "null"
					)
						return;
					const parsedItems = JSON.parse(subEvent.actual_items);
					if (!Array.isArray(parsedItems)) return;
					const numericItems = parsedItems.filter(
						(item: any) => !isNaN(item),
					);
					itemNumbers.push(
						...numericItems.map((x: any) => String(x)),
					);
				} catch {}
			});
		});
		return [...new Set(itemNumbers)];
	};

	const fetchSchedules = async (itemNumbers: string[]) => {
		if (itemNumbers.length === 0) return;
		try {
			const response = await globalAxios.get(`${SERVER_URL}/schedules`, {
				params: { itemNums: itemNumbers },
			});
			setSchedules(response.data);
		} catch (error) {
			console.error("Error fetching schedules:", error);
		}
	};

	useEffect(() => {
		const itemNumbers = extractItemNumbersFromRedux(events);
		fetchSchedules(itemNumbers);
	}, [events]);

	/* ----------------- Events ----------------- */
	const handleEventClick = React.useCallback(
		(clickInfo: any) => {
			const eProps = clickInfo.event.extendedProps;

			// -------------------------
			// PERSONAL EVENT
			// -------------------------
			if (eProps?.isPersonalEvent) {
				dispatch(setSelectedPersonal(eProps.personal));
				dispatch(setSelectedEntityType("createPersonal"));
				return;
			}

			// -------------------------
			// SESSION / REGULAR SESSION
			// -------------------------
			const { title, id, start, end, extendedProps } = clickInfo.event;

			dispatch(
				setSelectedSession({
					title,
					id,
					start,
					end,
					surgeon: {
						...extendedProps.surgeon,
						name: `${extendedProps.surgeon.first_name} ${extendedProps.surgeon.last_name}`,
					},
					hospital: extendedProps.hospital,
					events: extendedProps.events,
				}),
			);

			dispatch(setSelectedEntityType("session"));
		},
		[dispatch],
	);

	const convertEventsFormat = React.useCallback(
		(view: string, eventsArray: any[], usr: any) =>
			formatEvents(view, eventsArray, usr, schedules),
		[schedules],
	);

	// --------------------------------------------------------------------------------------------------
	// HANDLES Weekly, Daily, List View Styling - Monthly is handled in decorateDayCell / writeMonthSlotEvents /
	//--------------------------------------------------------------------------------------------------
	const renderEventContent = (eventInfo: any) => {
		const { content, backgroundColour } = contentToDisplay(
			user.settings,
			eventInfo,
			currentLens,
			schedules,
		);

		const isPersonalEvent =
			eventInfo.event.extendedProps?.isPersonalEvent === true;

		if (isPersonalEvent) {
			return (
				<div
					className="personal-event-content"
					style={{
						backgroundColor: backgroundColour,
						borderColor: backgroundColour,
					}}
				>
					{content}
				</div>
			);
		}

		const isRegularSession =
			eventInfo.event.extendedProps?.isRegularSession === true;

		if (isRegularSession) {
			return (
				<div
					className="regular-session-content"
					style={{
						backgroundColor: `${backgroundColour}80`,
					}}
				>
					{content}
				</div>
			);
		}

		const isFundingDetailView =
			currentLens === "fundingStatus" &&
			(eventInfo.view.type === "timeGridWeek" ||
				eventInfo.view.type === "timeGridDay");

		if (isFundingDetailView) {
			return (
				<div
					className="funding-session-event-content"
					style={{
						backgroundColor: `${backgroundColour}80`,
					}}
				>
					{content}
				</div>
			);
		}

		return (
			<div
				className="session-event-content"
				style={{
					backgroundColor: backgroundColour,
					borderColor: backgroundColour,
					color: "white",
					padding: "2px 4px",
					width: "100%",
				}}
			>
				{content}
			</div>
		);
	};

	const derivedRegularSessions = useMemo(() => {
		if (!regularSessions.length) return [];

		return expandRegularSessions({
			regularSessions,
			realLists: events,
			rangeStart: visibleRange.start,
			rangeEnd: visibleRange.end,
			getWeekLetter: (date) => weekLetterFromDate(date, anchorDate),
		});
	}, [regularSessions, events, visibleRange, anchorDate]);

	const personalEventsForCalendar = useMemo(() => {
		const results: any[] = [];

		const rangeStart = toLocalMidnight(visibleRange.start);
		const rangeEnd = toLocalMidnight(visibleRange.end);

		const pushPersonalEvent = (personal: any, dateStr: string) => {
			const isAllDay = Boolean(personal.all_day);
			const isRecurring = Boolean(personal.recurrence_rule);

			const endDate = personal.end_date || dateStr;

			const personalColour = setEventColor(user.settings, "main", {
				isPersonalEvent: true,
				isRegularPersonal: isRecurring,
			});

			results.push({
				id: isRecurring
					? `personal-${personal.id}-${dateStr}`
					: `personal-${personal.id}`,
				title: personal.title,

				start: isAllDay
					? `${dateStr}T08:00:00`
					: `${dateStr}T${personal.start_time}:00`,

				end: isAllDay
					? `${endDate}T22:30:00`
					: `${endDate}T${personal.end_time}:00`,

				allDay: false,

				color: personalColour,
				backgroundColor: personalColour,
				borderColor: personalColour,

				isPersonalEvent: true,
				isRegularPersonal: isRecurring,
				personalEventId: personal.id,
				personal,
			});
		};

		personals.forEach((personal) => {
			const isRecurring = Boolean(personal.recurrence_rule);

			if (!personal.start_date) return;

			if (!isRecurring) {
				const personalDate = parseISODateLocal(personal.start_date);

				if (personalDate >= rangeStart && personalDate < rangeEnd) {
					pushPersonalEvent(personal, personal.start_date);
				}

				return;
			}

			let occurrenceDate = parseISODateLocal(personal.start_date);

			while (occurrenceDate < rangeEnd) {
				if (occurrenceDate >= rangeStart) {
					pushPersonalEvent(personal, toISODate(occurrenceDate));
				}

				const nextOccurrence = getNextPersonalOccurrenceDate(
					occurrenceDate,
					personal.recurrence_rule,
				);

				if (!nextOccurrence) break;

				if (nextOccurrence.getTime() <= occurrenceDate.getTime()) break;

				occurrenceDate = nextOccurrence;
			}
		});

		return results;
	}, [personals, visibleRange, user.settings]);

	// Apply surgeon specific billing rules
	const surgeonBillingRulesBySurgeonId = useAppSelector(
		(state) => state.surgeonBillingRules.rulesBySurgeonId,
	);

	const surgeonBillingRules = useMemo(() => {
		return Object.values(surgeonBillingRulesBySurgeonId).flat();
	}, [surgeonBillingRulesBySurgeonId]);

	// Combine sessions with regular sessions.
	const combinedSessions = useMemo(() => {
		const rawSessions = [...events, ...derivedRegularSessions];

		return applySurgeonBillingRulesToSessions(
			rawSessions,
			surgeonBillingRules,
		);
	}, [events, derivedRegularSessions, surgeonBillingRules]);

	// Convert combined to event format.
	const sessionCalendarEvents = useMemo(() => {
		return convertEventsFormat(currentLens, combinedSessions, user);
	}, [convertEventsFormat, currentLens, combinedSessions, user]);

	const calendarEvents = useMemo(() => {
		if (currentLens === "main") {
			return [...sessionCalendarEvents, ...personalEventsForCalendar];
		}

		return sessionCalendarEvents;
	}, [currentLens, sessionCalendarEvents, personalEventsForCalendar]);

	const isMonthLikeView =
		currentView === "fourWeek" || currentView === "dayGridMonth";

	const monthEventsByDate = useMemo(() => {
		const map = new Map<string, any[]>();

		calendarEvents.forEach((event: any) => {
			if (!event?.start) return;

			const dateKey = String(event.start).slice(0, 10);
			if (!dateKey) return;

			if (!map.has(dateKey)) {
				map.set(dateKey, []);
			}

			map.get(dateKey)?.push(event);
		});

		return map;
	}, [calendarEvents]);

	/* ----------------- Title & view tracking ----------------- */
	const [calendarTitle, setCalendarTitle] = useState("");
	const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
	const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
	const [isMonthlyView, setIsMonthlyView] = useState(true);

	const handleDatesSet = (dateInfo: any) => {
		const { start, end, view } = dateInfo;

		setCalendarTitle(dateInfo.view.title);
		setCurrentMonth(start.getMonth());
		setCurrentYear(start.getFullYear());
		setIsMonthlyView(view.type === "fourWeek");
		dispatch(setCalendarView(view.type));

		// For regular session rendering.
		const nextStart = new Date(start);
		const nextEnd = new Date(end);

		setVisibleRange((prev) => {
			const unchanged =
				sameDay(prev.start, nextStart) && sameDay(prev.end, nextEnd);

			if (unchanged) {
				console.debug(
					"[CAL] visibleRange unchanged, skipping setVisibleRange",
				);
				return prev;
			}

			console.debug("[CAL] updating visibleRange", {
				prevStart: prev.start,
				prevEnd: prev.end,
				nextStart,
				nextEnd,
			});

			return {
				start: nextStart,
				end: nextEnd,
			};
		});
		// ── Week-cycle label (compute BEFORE early return)
		if (view.type === "timeGridWeek") {
			const weekStart = startOfWeek(start, { weekStartsOn: 1 });
			const letter = weekLetterFromDate(weekStart, anchorRef.current);
			console.debug("[WeekLabel] week view", { weekStart, letter });
			dispatch(setWeekCycleLabel(letter));
		} else if (view.type === "timeGridDay") {
			const letter = weekLetterFromDate(start, anchorRef.current);
			console.debug("[WeekLabel] day view", { day: start, letter });
			dispatch(setWeekCycleLabel(letter));
		} else {
			dispatch(setWeekCycleLabel(null));
		}

		const viewMonTs = startOfMonday(start).getTime();
		if (lastGotoRef.current === viewMonTs) {
			lastGotoRef.current = null;
			return;
		}

		// 🩹 Reset fourWeek block to anchor-aligned start when switching back
		if (view.type === "fourWeek") {
			const anchorMonday = startOfMonday(anchorDate);
			const snap = snapBlockStart(anchorMonday, focusMonday);
			const api = (calendarRef.current as any)?.getApi?.();
			if (api && api.view?.type === "fourWeek") {
				const viewStart =
					api.view.currentStart &&
					startOfMonday(api.view.currentStart);
				if (!viewStart || viewStart.getTime() !== snap.getTime()) {
					console.debug(
						"[ViewChange] restoring fourWeek block to",
						snap,
					);
					lastGotoRef.current = snap.getTime();
					api.gotoDate(snap);
				}
			}
		}

		if (view.type === "timeGridDay") {
			const prev = currentDate ? new Date(currentDate) : null;
			const changed =
				!prev ||
				prev.getFullYear() !== start.getFullYear() ||
				prev.getMonth() !== start.getMonth() ||
				prev.getDate() !== start.getDate();
			if (changed) dispatch(setCalendarDate(start.toISOString()));
		} else if (view.type === "timeGridWeek") {
			const weekStart = startOfWeek(start, { weekStartsOn: 1 });
			if (
				!currentDate ||
				!isSameWeek(new Date(currentDate), weekStart, {
					weekStartsOn: 1,
				})
			) {
				dispatch(setCalendarDate(weekStart.toISOString()));
			}
		}
	};
	// Keep Week Letter Label for Week/Day View in sync when changed
	useEffect(() => {
		const api = (calendarRef.current as any)?.getApi?.();
		if (!api) return;
		const vt: string | undefined = api.view?.type;
		if (vt !== "timeGridWeek" && vt !== "timeGridDay") return;

		const base: Date = api.getDate?.() ?? new Date();
		const letter =
			vt === "timeGridWeek"
				? weekLetterFromDate(
						startOfWeek(base, { weekStartsOn: 1 }),
						anchorRef.current,
					)
				: weekLetterFromDate(base, anchorRef.current);

		console.debug("[WeekLabel] anchor changed", { vt, letter });
		dispatch(setWeekCycleLabel(letter));
	}, [anchorDate, dispatch]);

	/* ----------------- Delete events by month ----------------- */
	const handleDeleteEvents = async () => {
		setDeletingEvents(true);
		const formattedDate = `${currentYear}-${(currentMonth + 1)
			.toString()
			.padStart(2, "0")}`;
		try {
			const response = await globalAxios.delete(
				`${SERVER_URL}/user/sessions`,
				{
					data: { date: formattedDate },
				},
			);
			if (response.status === 200) {
				dispatch(deleteEventsByMonth(formattedDate));
			} else {
				console.error(
					"Failed to delete events:",
					response.data.message,
				);
			}
		} catch (e) {
			console.error("Error deleting events:", e);
		} finally {
			setDeletingEvents(false);
		}
	};

	/* ----------------- Sync 4-week block ----------------- */
	const isSameYMD = (a: Date, b: Date) =>
		a.getFullYear() === b.getFullYear() &&
		a.getMonth() === b.getMonth() &&
		a.getDate() === b.getDate();

	// Replace your whole sync effect with this:
	useEffect(() => {
		const api = (calendarRef.current as any)?.getApi?.();
		if (!api) return;

		const viewType: string | undefined = (api as any).view?.type;
		const currentStart: Date | undefined = (api as any).view?.currentStart;

		if (viewType === "fourWeek") {
			// Month (4-week) view → align to blockStart (top-left Monday)
			const target = blockStart;
			const viewStartMonday = currentStart && startOfMonday(currentStart);
			if (
				!viewStartMonday ||
				viewStartMonday.getTime() !== target.getTime()
			) {
				lastGotoRef.current = target.getTime();
				api.gotoDate(target);
			}
			return;
		}

		// Base: prefer currentDate (Redux), else FC cursor
		const base = currentDate
			? new Date(currentDate)
			: (api.getDate?.() ?? new Date());

		if (viewType === "timeGridWeek") {
			// Week view → Monday of that week
			const weekStart = startOfWeek(base, { weekStartsOn: 1 });
			const viewStartMonday = currentStart && startOfMonday(currentStart);
			if (
				!viewStartMonday ||
				viewStartMonday.getTime() !== weekStart.getTime()
			) {
				lastGotoRef.current = weekStart.getTime();
				api.gotoDate(weekStart);
			}
			return;
		}

		if (viewType === "timeGridDay") {
			// Day view → exact day (no Monday normalisation)
			const target = toLocalMidnight(base);
			if (!currentStart || !isSameYMD(currentStart, target)) {
				lastGotoRef.current = target.getTime();
				api.gotoDate(target);
			}
			return;
		}

		if (viewType === "listMonth") {
			// List month → first of month
			const target = new Date(base.getFullYear(), base.getMonth(), 1);
			const sameMonth =
				currentStart &&
				currentStart.getFullYear() === target.getFullYear() &&
				currentStart.getMonth() === target.getMonth();
			if (!sameMonth) {
				lastGotoRef.current = target.getTime();
				api.gotoDate(target);
			}
			return;
		}
	}, [currentDate, anchorDate, blockStart]);

	/* ----------------- Hydrate anchor from settings (once) ----------------- */
	const didHydrateRef = useRef(false);
	useEffect(() => {
		if (didHydrateRef.current) return;

		let settings = user?.settings;
		if (typeof settings === "string") {
			try {
				settings = JSON.parse(settings);
			} catch {}
		}
		const iso: string | undefined = settings?.calendar?.anchorDate;

		if (iso) {
			const monday = startOfMonday(parseISODateLocal(iso));
			console.debug("[Anchor][hydrate-once]", { iso });
			setAnchorDate(monday);
		} else {
			console.debug("[Anchor][hydrate-once] none in server settings");
		}

		didHydrateRef.current = true;
	}, [user?.settings]);

	/* ----------------- Context menu ----------------- */
	// Before: const [contextMenu, setContextMenu] = useState<null | { date: Date }>(null);

	const [menuOpen, setMenuOpen] = useState(false);
	const [menuDate, setMenuDate] = useState<Date | null>(null);

	const handleDayRightClick = (e: MouseEvent, info: any) => {
		e.preventDefault();
		e.stopPropagation();
		setMenuDate(info.date);
		setMenuOpen(true);
		console.debug("[CtxMenu] open (bottom-right)", { date: info.date });
	};

	const handleMenuClose = () => {
		setMenuOpen(false); // keep menuDate until exit animation ends
	};

	const handleCreatePersonalFromMenu = () => {
		if (!menuDate) return;

		dispatch(setCreateDate(toISODate(menuDate)));
		dispatch(setSelectedEntityType("createPersonal"));
		handleMenuClose();
	};

	/* ----------------- Nav helpers ----------------- */
	const navigateByView = (direction: 1 | -1) => {
		const api = (calendarRef.current as any)?.getApi?.();
		const viewType: string | undefined = api?.view?.type;

		// Base date: FullCalendar’s current “cursor” date
		const baseDate: Date = api?.getDate?.() ?? new Date();

		let target: Date;

		switch (viewType) {
			case "timeGridDay":
				target = addDaysSafe(baseDate, direction * 1);
				break;
			case "timeGridWeek": {
				const weekStart = startOfWeek(baseDate, { weekStartsOn: 1 });
				target = addDaysSafe(weekStart, direction * 7);
				break;
			}
			case "listMonth":
				// Jump whole months for list view
				target = addMonthsSafe(baseDate, direction * 1);
				break;
			case "fourWeek":
			default: {
				// Month view = 4-week block jumps
				const mon = startOfMonday(baseDate);
				target = addDaysSafe(mon, direction * 28);
				break;
			}
		}

		dispatch(setCalendarDate(target.toISOString()));
	};

	const goTodayByView = () => {
		const api = (calendarRef.current as any)?.getApi?.();
		const viewType: string | undefined = api?.view?.type;
		const today = new Date();

		let target: Date;

		switch (viewType) {
			case "timeGridDay":
				target = today; // exact day
				break;
			case "timeGridWeek":
				target = startOfWeek(today, { weekStartsOn: 1 });
				break;
			case "listMonth":
				target = new Date(today.getFullYear(), today.getMonth(), 1);
				break;
			case "fourWeek":
			default:
				target = startOfMonday(today);
				break;
		}

		dispatch(setCalendarDate(target.toISOString()));
	};

	const handleViewChange = (viewType: string) => {
		const api = (calendarRef.current as any)?.getApi?.();

		if (!api) return;

		const selectedDate = currentDate
			? toLocalMidnight(currentDate)
			: (api.getDate?.() ?? new Date());

		console.debug("[ViewChange]", {
			viewType,
			selectedDate,
		});

		api.changeView(viewType, selectedDate);
	};

	/* ----------------- Anchor change (persist) ----------------- */
	const handleAnchorChange = async (value: Date | null) => {
		if (!value) return;

		const monday = startOfMonday(value);
		const nextISO = toISODate(monday);
		const prevISO = toISODate(anchorDate);

		console.debug("[Anchor][change] request", { prevISO, nextISO });

		if (nextISO === prevISO) {
			setAnchorDate(monday); // normalise to Monday
			return;
		}

		const prevAnchor = anchorDate;
		setAnchorDate(monday);
		dispatch(updateCalendarAnchorDate(nextISO));
		dispatch(setCalendarSliceAnchorDate(nextISO));

		setSavingAnchor(true);
		try {
			await globalAxios.put(
				`${SERVER_URL}/user/settings/calendar/setAnchorDate`,
				{
					anchorDate: nextISO,
				},
			);
			console.debug("[Anchor][save] success", { nextISO });

			// Force day cells to rebuild so labels recompute against new anchor
			const api = (calendarRef.current as any)?.getApi?.();
			if (api) {
				console.debug("[Anchor][sync-FC] changeView same type", {
					viewType: api.view.type,
				});
				api.changeView(api.view.type, api.getDate());
			}
		} catch (err) {
			console.error("[Settings] anchorDate save FAILED:", err);
			setAnchorDate(prevAnchor);
			dispatch(updateCalendarAnchorDate(toISODate(prevAnchor)));
			dispatch(setCalendarSliceAnchorDate(nextISO));
		} finally {
			setSavingAnchor(false);
		}
	};

	/* ----------------- Day decoration (A/B/C/D chips) ----------------- */
	// Track currently-mounted day frames so we can refresh chips in-place
	const dayFramesRef = useRef<Set<HTMLElement>>(new Set());

	const getCellDateFromFrame = (frame: HTMLElement): Date | null => {
		const host = frame.closest("[data-date]") as HTMLElement | null;
		const iso = host?.getAttribute("data-date"); // "YYYY-MM-DD"
		if (!iso) return null;
		const [y, m, d] = iso.split("-").map(Number);
		return new Date(y, (m ?? 1) - 1, d ?? 1);
	};

	// Render (or re-render) chips into a frame for a given date and anchor
	const writeWeekChips = (
		frame: HTMLElement,
		date: Date,
		anchorMon: Date,
	) => {
		// Clean previous chips to prevent AABC
		frame.querySelectorAll(".week-row-labels").forEach((n) => n.remove());
		frame
			.querySelectorAll(".month-slot-container")
			.forEach((n) => n.remove());

		// Only Mondays get labels
		if (date.getDay() !== 1) return;

		const cellMon = startOfMonday(date);
		const letter = letterForMondayFromAnchor(cellMon, anchorMon);

		const { week, overlap } = isoWeekInfo(cellMon);
		const showWeek = overlap ? "53/1" : String(week);

		const wrap = document.createElement("div");
		wrap.className = "week-row-labels";
		Object.assign(wrap.style, {
			position: "absolute",
			left: "6px",
			bottom: "6px",
			display: "flex",
			gap: "6px",
			alignItems: "center",
			pointerEvents: "none",
		} as CSSStyleDeclaration);

		const chip = (text: string, emphasise = false) => {
			const el = document.createElement("div");
			el.textContent = text;
			Object.assign(
				el.style,
				emphasise
					? {
							fontSize: "10px",
							lineHeight: "1",
							padding: "3px 6px",
							borderRadius: "999px",
							fontWeight: "600",
							background: "rgba(33,150,243,0.10)",
							color: "#1e4976",
							border: "1px solid rgba(33,150,243,0.25)",
							pointerEvents: "none",
						}
					: ({
							fontSize: "10px",
							lineHeight: "1",
							padding: "3px 6px",
							borderRadius: "999px",
							fontWeight: "600",
							color: "rgba(0,0,0,0.7)",
							pointerEvents: "none",
						} as CSSStyleDeclaration),
			);
			return el;
		};

		wrap.appendChild(chip(letter, true));
		wrap.appendChild(chip(showWeek, false));
		frame.appendChild(wrap);

		console.debug("[Chips] write", {
			anchor: anchorMon.toDateString(),
			cell: cellMon.toDateString(),
			letter,
			isoWeek: showWeek,
		});
	};

	// Refresh all visible frames after an anchor change
	const refreshAllWeekChips = React.useCallback((anchorMonday: Date) => {
		dayFramesRef.current.forEach((frame) => {
			const cellDate = getCellDateFromFrame(frame);
			if (!cellDate) return;
			writeWeekChips(frame, cellDate, anchorMonday);
		});
		console.debug("[Chips] refreshAll", {
			frames: dayFramesRef.current.size,
			anchor: anchorMonday.toDateString(),
		});
	}, []);

	const createMonthSlotEventClickInfo = (event: any) => {
		const extendedProps = event.extendedProps ?? event;

		return {
			event: {
				title: event.title,
				id: event.id,
				start:
					typeof event.start === "string"
						? new Date(event.start)
						: event.start,
				end:
					typeof event.end === "string"
						? new Date(event.end)
						: event.end,
				extendedProps,
			},
		};
	};

	const writeMonthSlotEvents = React.useCallback(
		(frame: HTMLElement, date: Date) => {
			frame
				.querySelectorAll(".month-slot-container")
				.forEach((n) => n.remove());

			const isoDate = toISODate(date);
			const dayEvents = monthEventsByDate.get(isoDate) || [];

			if (dayEvents.length === 0) return;

			const slotContainer = document.createElement("div");
			slotContainer.className = "month-slot-container";

			dayEvents.forEach((event) => {
				const startTime = getTimeFromEvent(event, "start");
				const endTime = getTimeFromEvent(event, "end");
				const { slot, span } = getMonthBlockPlacement(
					startTime,
					endTime,
				);

				const block = document.createElement("button");
				block.type = "button";
				block.className = "month-slot-event-block";
				block.textContent = getMonthEventText(event);

				const isRegularSession =
					event.isRegularSession === true ||
					event.extendedProps?.isRegularSession === true;
				const baseColor =
					event.color ||
					event.backgroundColor ||
					event.extendedProps?.color ||
					event.extendedProps?.backgroundColor ||
					"#1976d2";

				const regularSessionColor =
					currentLens === "main" ? baseColor : "#9e9e9e";

				const appliedColor = isRegularSession
					? `${regularSessionColor}80`
					: baseColor;

				block.style.backgroundColor = appliedColor;
				block.style.gridRow = `${slot + 1} / span ${span}`;

				if (event.isRegularSession) {
					block.classList.add("month-slot-event-regular");
				}

				if (event.isPersonalEvent) {
					block.classList.add("month-slot-event-personal");
				}

				block.onclick = (e) => {
					e.preventDefault();
					e.stopPropagation();
					handleEventClick(createMonthSlotEventClickInfo(event));
				};

				slotContainer.appendChild(block);
			});

			frame.appendChild(slotContainer);
		},
		[monthEventsByDate, handleEventClick, currentLens],
	);

	const refreshAllMonthSlotEvents = React.useCallback(() => {
		dayFramesRef.current.forEach((frame) => {
			const cellDate = getCellDateFromFrame(frame);
			if (!cellDate) return;
			writeMonthSlotEvents(frame, cellDate);
		});
	}, [writeMonthSlotEvents]);

	const decorateDayCell = (info: any) => {
		if (info.view.type !== "fourWeek") return;

		const frame: HTMLElement =
			(info.el.querySelector(".fc-daygrid-day-frame") as HTMLElement) ||
			info.el;

		// Ensure positioned container for chips
		const pos = getComputedStyle(frame).position;
		if (!pos || pos === "static") frame.style.position = "relative";

		// Track the frame so we can refresh later
		dayFramesRef.current.add(frame);

		// Initial render against current anchor
		const anchorMon = startOfMonday(anchorRef.current);
		writeWeekChips(frame, info.date, anchorMon);
		writeMonthSlotEvents(frame, info.date);

		if (!(frame as any)._ctxBound) {
			const onCtx = (ev: MouseEvent) => {
				ev.preventDefault();
				ev.stopPropagation();
				handleDayRightClick(ev, info); // your existing handler
			};
			frame.addEventListener("contextmenu", onCtx);
			(frame as any)._ctxBound = true;
			(frame as any)._ctxCleanup = () =>
				frame.removeEventListener("contextmenu", onCtx);
		}

		console.debug("[Decorate] mount", {
			date: info.date.toDateString(),
			trackedFrames: dayFramesRef.current.size,
		});
	};

	const cleanupDayCell = (info: any) => {
		const frame: HTMLElement =
			(info.el.querySelector(".fc-daygrid-day-frame") as HTMLElement) ||
			info.el;
		frame.querySelectorAll(".week-row-labels").forEach((n) => n.remove());
		frame
			.querySelectorAll(".month-slot-container")
			.forEach((n) => n.remove());
		dayFramesRef.current.delete(frame);
		(frame as any)._ctxCleanup?.();
		(frame as any)._ctxBound = false;
		console.debug("[Decorate] unmount", {
			date: info.date?.toDateString?.(),
			trackedFrames: dayFramesRef.current.size,
		});
	};

	useEffect(() => {
		refreshAllWeekChips(startOfMonday(anchorDate));
	}, [anchorDate, refreshAllWeekChips]);

	useEffect(() => {
		refreshAllMonthSlotEvents();
	}, [monthEventsByDate, refreshAllMonthSlotEvents]);

	/* ----------------- Route to sub-views ----------------- */
	switch (selectedEntityType) {
		case "session":
			if (selectedSession) return <SessionView />;
			break;
		case "event":
			if (selectedEvent) return <EventView />;
			break;
		case "patient":
			if (selectedPatient) return <PatientView />;
			break;
		case "createPersonal":
			return <PersonalCreateView />;
		default:
			break;
	}

	const LensDropdown = () => (
		<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
			<Typography
				variant="body1"
				fontWeight={"bold"}
				sx={{ minWidth: "40px" }}
			>
				Lens
			</Typography>
			<Select
				variant="outlined"
				size="small"
				value={currentLens}
				onChange={(e) => dispatch(setCalendarLens(e.target.value))}
				sx={{
					minWidth: 180,
					"& .MuiSelect-select": {
						paddingTop: "4px",
						paddingBottom: "4px",
					},
				}}
			>
				{lensOptions.map((option) => (
					<MenuItem key={option.lens} value={option.lens}>
						{option.label}
					</MenuItem>
				))}
			</Select>
		</Box>
	);

	return (
		<>
			<Box
				sx={{
					display: "flex",
					flexDirection: "column",
					height: "80vh",
					maxHeight: "80vh",
				}}
			>
				{/* Header */}
				<Box
					sx={{
						flexShrink: 0,
						display: "flex",
						alignItems: "center",
						gap: 2,
						px: 2,
						mb: 2,
						"& > .hdr": { flex: 1, minWidth: 0 },
					}}
				>
					{/* LEFT: prev/next/today */}
					<Box
						className="hdr"
						sx={{
							display: "flex",
							gap: 1,
							justifyContent: "flex-start",
						}}
					>
						<IconButton
							aria-label="previous"
							onClick={() => navigateByView(-1)}
							size="small"
						>
							<ChevronLeftIcon />
						</IconButton>

						<IconButton
							aria-label="next"
							onClick={() => navigateByView(1)}
							size="small"
						>
							<ChevronRightIcon />
						</IconButton>

						<Button
							variant="outlined"
							size="small"
							onClick={goTodayByView}
						>
							Today
						</Button>
					</Box>

					{/* CENTRE: calendar title */}
					<Box
						className="hdr"
						sx={{
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
							gap: 1.5,
						}}
					>
						<Typography
							variant="h5"
							fontWeight="bold"
							sx={{
								textAlign: "center",
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{calendarTitle}
						</Typography>

						{["timeGridWeek", "timeGridDay"].includes(
							currentView,
						) &&
							weekCycleLabel && (
								<Box
									component="span"
									className="week-letter-chip"
								>
									{weekCycleLabel}
								</Box>
							)}
					</Box>

					{/* RIGHT: view switchers */}
					<Box
						className="hdr"
						sx={{
							display: "flex",
							gap: 1,
							justifyContent: "flex-end",
							alignItems: "center",
						}}
					>
						<ButtonGroup variant="outlined" size="small">
							<Button
								onClick={() => handleViewChange("fourWeek")}
							>
								Month
							</Button>

							<Button
								onClick={() => handleViewChange("timeGridWeek")}
							>
								Week
							</Button>

							<Button
								onClick={() => handleViewChange("timeGridDay")}
							>
								Day
							</Button>

							<Button
								onClick={() => handleViewChange("listMonth")}
							>
								List
							</Button>
						</ButtonGroup>
					</Box>
				</Box>

				{/* Calendar */}
				<Box
					sx={{
						flex: 1,
						minHeight: 0,
						"& .fc": { height: "100%" },
						"& .fc-event": { cursor: "pointer" },
						"& .fc-daygrid-day-top": {
							display: "flex",
							alignItems: "center",
							minHeight: "18px",
						},
					}}
				>
					<FullCalendar
						ref={calendarRef as any}
						showNonCurrentDates={false}
						plugins={[
							dayGridPlugin,
							timeGridPlugin,
							listPlugin,
							interactionPlugin,
						]}
						initialView={
							currentView === "dayGridMonth"
								? "fourWeek"
								: currentView
						}
						views={{
							fourWeek: {
								type: "dayGrid",
								duration: { weeks: 4 },
								buttonText: "4 weeks",
								visibleRange: () => {
									const start = blockStart;
									const end = new Date(start);
									end.setDate(end.getDate() + 28); // end exclusive
									return { start, end };
								},
								dayMaxEventRows: 3,
								moreLinkClick: "popover",
							},
						}}
						initialDate={blockStart}
						slotMinTime="08:00:00"
						slotMaxTime="22:00:00"
						slotLabelInterval="05:00:00"
						headerToolbar={false}
						firstDay={1}
						editable={true}
						eventStartEditable={
							currentView !== "dayGridMonth" &&
							currentView !== "fourWeek"
						}
						eventDurationEditable={
							currentView !== "dayGridMonth" &&
							currentView !== "fourWeek"
						}
						expandRows={true}
						allDaySlot={false}
						events={isMonthLikeView ? [] : calendarEvents}
						eventClick={handleEventClick}
						datesSet={handleDatesSet}
						/* removed obsolete dayGridMonth tweak */
						eventContent={renderEventContent}
						eventClassNames={(arg) => {
							const classes = [];

							if (arg.event.extendedProps?.isRegularSession) {
								classes.push("regular-session-event");
							}

							if (arg.event.extendedProps?.isPersonalEvent) {
								classes.push("personal-event");
							}

							return classes;
						}}
						eventTimeFormat={{
							hour: "2-digit",
							minute: "2-digit",
							hour12: false,
						}}
						dayCellDidMount={decorateDayCell}
						dayCellWillUnmount={cleanupDayCell}
					/>
				</Box>

				{/* Context menu */}
			</Box>

			{/* Footer */}
			<Box
				sx={{
					flexShrink: 0,
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					gap: 1,
					px: 2,
					py: 1,
					borderTop: (theme) => `1px solid ${theme.palette.divider}`,
					backgroundColor: "background.paper",
				}}
			>
				<LensDropdown />

				{/* <Tooltip
					title="Set a reference for your 4-week cycle. This date defines Week A."
					placement="right"
					arrow
					enterDelay={300}
				> */}
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					<Typography variant="body1" fontWeight="bold">
						Anchor week
					</Typography>
					<LocalizationProvider
						dateAdapter={AdapterDateFns}
						adapterLocale={enGB}
					>
						<DatePicker
							value={anchorDate}
							onChange={handleAnchorChange}
							format="dd/MM/yyyy"
							disabled={savingAnchor}
							slotProps={{
								textField: {
									size: "small",
									variant: "outlined",
									sx: { minWidth: 170 },
								},
							}}
						/>
					</LocalizationProvider>
					<IconButton
						aria-label="About anchor week"
						aria-describedby={helpId}
						size="small"
						onClick={(e) => setHelpAnchorEl(e.currentTarget)}
					>
						<InfoOutlinedIcon fontSize="small" />
					</IconButton>

					<Popover
						id={helpId}
						open={helpOpen}
						anchorEl={helpAnchorEl}
						onClose={() => setHelpAnchorEl(null)}
						anchorOrigin={{
							vertical: "bottom",
							horizontal: "left",
						}}
						transformOrigin={{
							vertical: "top",
							horizontal: "left",
						}}
						PaperProps={{ sx: { p: 1.5, maxWidth: 300 } }}
					>
						<Typography variant="body2">
							Set a reference Monday for your 4-week cycle. The
							selected week is “A” and subsequent Mondays are
							labelled B, C, and D in order.
						</Typography>
					</Popover>
					{savingAnchor && (
						<CircularProgress
							size={18}
							sx={{ ml: 1 }}
							aria-label="Saving anchor week"
						/>
					)}
				</Box>
				{/* </Tooltip> */}

				{isAdmin && isMonthlyView && (
					<Button
						variant="contained"
						color="error"
						size="small"
						disableElevation
						sx={{ color: "white" }}
						onClick={handleDeleteEvents}
						disabled={deletingEvents}
					>
						{deletingEvents ? (
							<CircularProgress
								size={24}
								sx={{ color: "white" }}
							/>
						) : (
							"Delete Events"
						)}
					</Button>
				)}
			</Box>

			<Menu
				open={menuOpen}
				onClose={handleMenuClose}
				anchorReference="anchorEl"
				anchorEl={document.body}
				anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
				transformOrigin={{ vertical: "bottom", horizontal: "right" }}
				// keep it mounted so content doesn’t flicker during transitions
				keepMounted
				// clear the date only after the exit transition completes
				TransitionProps={{
					timeout: 0,
					onExited: () => setMenuDate(null),
				}}
				MenuListProps={{
					autoFocus: false, // avoid focus jumps that can feel like stutter
					disableListWrap: true,
				}}
				slotProps={{
					paper: {
						sx: {
							width: 200,
							boxShadow: "0 4px 16px rgba(0,0,0,0.18)",
							border: (theme) =>
								`1px solid ${theme.palette.divider}`,
						},
					},
				}}
			>
				<MenuItem
					disabled
					sx={{
						fontWeight: "bold",
						pointerEvents: "none",
						opacity: 0.8,
					}}
				>
					{menuDate
						? menuDate.toLocaleDateString(undefined, {
								weekday: "short",
								day: "numeric",
								month: "short",
								year: "numeric",
							})
						: "—"}
				</MenuItem>
				<MenuItem
					onClick={() => console.log("New Session for", menuDate)}
				>
					Create New Session
				</MenuItem>
				<MenuItem onClick={handleCreatePersonalFromMenu}>
					Create New Personal
				</MenuItem>
			</Menu>
			<PotentialRegularSessionDialog />
		</>
	);
};

export default Calendar;
